import React, { useState, useEffect } from "react";

import { Box, Grid, TextField, Typography, Avatar, Card, CardContent, IconButton } from "@mui/material";
import { red, grey } from "@mui/material/colors";

import {
  Comment as CommentIcon,
  Mode as ModeIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import { jwtDetails, headers } from "../utils/utils";

import message from "../../locales/message.json";

const Comments = ({ ticket, displayAlertMessage }) => {
  const [commentContent, setCommentContent] = useState(""); // State to track new comment
  const [isSaving, setIsSaving] = useState(false); // State to show saving state
  const [comments, setComments] = useState([]); // State to track the comment history
  const [editingCommentId, setEditingCommentId] = useState(null); // Track which comment is being edited
  const [editingContent, setEditingContent] = useState(""); // Track edited content

  const tokenDetails = jwtDetails();
  const userName = tokenDetails.preferred_username || "Unknown";

  const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : "?");

  // Fetch comment history
  const fetchComments = async () => {
    if (!ticket.id) {
      setComments([]); // Clear comments for a new ticket
      return;
    }

    try {
      const response = await fetch(
        process.env.REACT_APP_COMMENTS +
        `/comments/fetchComments/${ticket.id}/VYG-TICKET`,
        {
          method: "GET",
          headers: headers(),
        },
      );

      if (response.ok) {
        const data = await response.json();
        const sortedComments = data.sort(
          (a, b) => new Date(b.createdDate) - new Date(a.createdDate),
        );
        setComments(sortedComments);
      } else {
        console.error("Failed to fetch comments");
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  // Save a new comment
  const saveComment = async () => {
    if (!commentContent.trim()) return;

    const commentData = {
      resourceId: ticket.id,
      microserviceName: "VYG-TICKET",
      content: commentContent,
      createdBy: userName,
      createdDate: new Date().toISOString(),
    };

    setIsSaving(true);

    try {
      const response = await fetch(
        process.env.REACT_APP_COMMENTS + `/comments/save`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify(commentData),
        },
      );

      if (response.ok) {
        displayAlertMessage(message.comments);
        setCommentContent(""); // Clear the comment input
        fetchComments(); // Refresh comments
      } else {
        console.error("Failed to save comment");
      }
    } catch (error) {
      console.error("Error saving comment:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Edit a comment
  const saveEditedComment = async (id) => {
    if (!editingContent.trim()) return;

    const commentData = {
      content: editingContent,
      updatedDate: new Date().toISOString(), // Add the current date and time
    };

    try {
      const response = await fetch(
        process.env.REACT_APP_COMMENTS + `/comments/editComment/${id}`,
        {
          method: "PUT",
          headers: headers(),
          body: JSON.stringify(commentData),
        },
      );

      if (response.ok) {
        setEditingCommentId(null); // Exit edit mode
        setEditingContent(""); // Clear the editing content
        fetchComments(); // Refresh comments to reflect the changes
      } else {
        console.error("Failed to edit comment");
      }
    } catch (error) {
      console.error("Error editing comment:", error);
    }
  };

  // Delete a comment
  const deleteComment = async (id) => {
    try {
      const response = await fetch(
        process.env.REACT_APP_COMMENTS + `/comments/deleteComment/${id}`,
        {
          method: "DELETE",
          headers: headers(),
        },
      );

      if (response.ok) {
        fetchComments(); // Refresh comments
      } else {
        console.error("Failed to delete comment");
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  // Fetch comments when the component mounts or ticket changes
  useEffect(() => {
    fetchComments();
  }, [ticket.id]);

  return (
    <Grid item xs={12} sm={7.8} sx={{ ml: 2 }}>
      <div>
        {/* Header */}
        <Box sx={{ display: "flex", mt: 2 }}>
          <CommentIcon />
          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            sx={{ ml: 1 }}
          >
            <strong>Comments</strong>
          </Typography>
        </Box>

        {/* Input Section */}
        <Grid item xs={12} display="flex" alignItems="center" sx={{ mt: 2 }}>
          <Avatar
            sx={{
              bgcolor: "inherit",
              color: "white",
              background: red[500],
              marginRight: "4px",
              border: "1px solid white",
            }}
          >
            {getInitial(userName)}
          </Avatar>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter your comments"
            multiline
            rows={1}
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                saveComment();
              }
            }}
            onBlur={saveComment}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "rgb(204, 204, 204)",
                  borderWidth: "1px",
                },
              },
            }}
          />
        </Grid>

        <Box sx={{ mt: 1, mx: "auto" }}>
          {comments.length > 0 ? (
            comments.map((comment) => (
              <Card key={comment.id} sx={{ mb: 2, boxShadow: 2 }}>
                <CardContent sx={{ pb: "8px !important" }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <Avatar
                        sx={{
                          backgroundColor: red[500],
                          width: 36,
                          height: 36,
                          fontSize: "1rem",
                        }}
                      >
                        {getInitial(comment.createdBy)}
                      </Avatar>
                    </Grid>

                    <Grid item xs>
                      {editingCommentId === comment.id ? (
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          onBlur={() => saveEditedComment(comment.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              saveEditedComment(comment.id);
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="body1">{comment.content}</Typography>
                      )}

                      <Typography variant="caption" color={grey[600]}>
                        {comment.createdBy} •{" "}
                        {new Date(comment.createdDate + "Z").toLocaleString("en-IN", {
                          timeZone: "Asia/Kolkata", // Ensure it displays IST
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true, // 12-hour format with AM/PM
                        })}
                      </Typography>


                    </Grid>

                    {comment.createdBy === userName && (
                      <Grid item>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingCommentId(comment.id);
                            setEditingContent(comment.content);
                          }}
                        >
                          <ModeIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => deleteComment(comment.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            ))
          ) : (
            <></>
          )}
        </Box>

      </div>
    </Grid>
  );
};

export default Comments;
